import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { serverFetch } from "../utils/serverApi";
import {
  EXAMPLE_SUBJECT_COUNTS,
  settingsToForm,
  validateForm,
  formToPayload,
  isUnchanged,
  costFor,
  prizeLine,
  describeError,
  formatUpdatedAt,
} from "../utils/teamExamSettings";
import "./Mail.css";
import "./TeamExamSettingsPage.css";

const SETTINGS_PATH = "/api/admin/team-exam/settings";

const TeamExamSettingsPage = () => {
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: "ok" | "err", text }
  const mounted = useRef(true);
  const confirmRef = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const data = await serverFetch(SETTINGS_PATH);
      if (!mounted.current) return;
      if (!data || !data.settings) throw new Error("The server returned no settings.");
      setSaved(data.settings);
      setForm(settingsToForm(data.settings));
    } catch (e) {
      if (mounted.current) setLoadError(describeError(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (confirmOpen && confirmRef.current) confirmRef.current.focus();
  }, [confirmOpen]);

  const errors = useMemo(() => (form ? validateForm(form) : {}), [form]);
  const valid = Object.keys(errors).length === 0;
  const unchanged = !form || isUnchanged(form, saved);
  const payload = useMemo(() => (form && valid ? formToPayload(form) : null), [form, valid]);

  const change = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFeedback(null);
    setConfirmOpen(false);
  };

  const save = async () => {
    if (saving || !payload) return;
    setConfirmOpen(false);
    setSaving(true);
    setFeedback(null);
    try {
      const data = await serverFetch(SETTINGS_PATH, { method: "PUT", body: payload });
      if (!mounted.current) return;
      const next = data?.settings || payload;
      setSaved(next);
      setForm(settingsToForm(next));
      setFeedback({ type: "ok", text: "Settings saved. They apply to rooms created from now on." });
    } catch (e) {
      if (mounted.current) setFeedback({ type: "err", text: describeError(e) });
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const updatedAt = saved ? formatUpdatedAt(saved.updated_at ?? saved.updatedAt) : "";
  const updatedBy = saved ? saved.updated_by ?? saved.updatedBy : "";

  if (loading) {
    return (
      <div className="mail-page te-page" role="status" aria-live="polite">
        <h1>Team Exam Settings</h1>
        <span className="te-sr">Loading settings...</span>
        <div className="te-skeleton" />
        <div className="te-skeleton" />
        <div className="te-skeleton" />
      </div>
    );
  }

  if (loadError || !form) {
    return (
      <div className="mail-page te-page">
        <h1>Team Exam Settings</h1>
        <div className="mail-banner err" role="alert">{loadError || "Settings could not be loaded."}</div>
        <div className="mail-actions">
          <button type="button" className="mail-btn primary" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }

  const field = (key, label, { disabled = false, hint } = {}) => (
    <div>
      <label htmlFor={`te-${key}`}>{label}</label>
      <input
        id={`te-${key}`}
        type="text"
        inputMode="numeric"
        value={form[key]}
        onChange={(e) => change(key, e.target.value)}
        disabled={disabled || saving}
        aria-invalid={errors[key] ? "true" : "false"}
        aria-describedby={`te-${key}-msg`}
      />
      <div id={`te-${key}-msg`} className={errors[key] ? "te-error" : "mail-hint"}>
        {errors[key] || hint || ""}
      </div>
    </div>
  );

  const place = (name, label, enabledKey, coinsKey) => (
    <div>
      <label className="te-place" htmlFor={`te-${enabledKey}`}>
        <input
          id={`te-${enabledKey}`}
          type="checkbox"
          checked={form[enabledKey]}
          onChange={(e) => change(enabledKey, e.target.checked)}
          disabled={saving}
        />
        {label}
      </label>
      {field(coinsKey, `${name} place coins`, { disabled: !form[enabledKey], hint: form[enabledKey] ? "" : "Switched off: nobody wins this prize." })}
    </div>
  );

  const rateKeys = valid ? payload : null;

  return (
    <div className="mail-page te-page">
      <h1>Team Exam Settings</h1>
      <div className="mail-box">
        Applies to rooms created from now on. Rooms that already exist keep the costs and prizes they were created with.
      </div>
      {(updatedAt || updatedBy) && (
        <p className="mail-hint">
          Last updated{updatedBy ? ` by ${updatedBy}` : ""}{updatedAt ? ` on ${updatedAt}` : ""}.
        </p>
      )}

      <form onSubmit={(e) => { e.preventDefault(); if (valid && !unchanged && !saving) setConfirmOpen(true); }} noValidate>
        <div className="te-grid">
          {field("hostRate", "Hosting cost per subject (coins)")}
          {field("joinRate", "Joining cost per subject (coins)")}
        </div>
        <div className="te-grid">
          {field("firstCoins", "1st place coins (always on)")}
          <div />
          {place("2nd", "Pay a 2nd place prize", "secondEnabled", "secondCoins")}
          {place("3rd", "Pay a 3rd place prize", "thirdEnabled", "thirdCoins")}
        </div>

        <div aria-live="polite">
          {feedback && <div className={`mail-banner ${feedback.type}`} role={feedback.type === "err" ? "alert" : "status"}>{feedback.text}</div>}
        </div>

        <div className="mail-actions">
          <button type="submit" className="mail-btn primary" disabled={!valid || unchanged || saving}>
            {saving ? "Saving..." : "Save changes"}
          </button>
          <button type="button" className="mail-btn" disabled={unchanged || saving} onClick={() => { setForm(settingsToForm(saved)); setFeedback(null); setConfirmOpen(false); }}>
            Discard changes
          </button>
        </div>

        {confirmOpen && rateKeys && (
          <div className="mail-box" role="alertdialog" aria-labelledby="te-confirm-title" aria-describedby="te-confirm-body">
            <strong id="te-confirm-title">Save these settings?</strong>
            <p id="te-confirm-body" className="mail-hint">
              This changes what players pay and win in every new room. Hosting {rateKeys.host_rate_per_subject} and joining {rateKeys.join_rate_per_subject} coins per subject. {prizeLine(rateKeys)}.
            </p>
            <div className="mail-actions">
              <button type="button" className="mail-btn" onClick={() => setConfirmOpen(false)}>Cancel</button>
              <button type="button" className="mail-btn primary" ref={confirmRef} onClick={save} disabled={saving}>Confirm and save</button>
            </div>
          </div>
        )}
      </form>

      <div className="mail-box" aria-labelledby="te-preview-title" role="region">
        <strong id="te-preview-title">Preview (what players see)</strong>
        {payload ? (
          <>
            <table className="te-table">
              <thead>
                <tr><th scope="col">Subjects</th><th scope="col">Host pays</th><th scope="col">Joiner pays</th></tr>
              </thead>
              <tbody>
                {EXAMPLE_SUBJECT_COUNTS.map((n) => (
                  <tr key={n}>
                    <td>{n}</td>
                    <td>{costFor(payload.host_rate_per_subject, n)} coins</td>
                    <td>{costFor(payload.join_rate_per_subject, n)} coins</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ marginTop: ".5rem" }}>Prizes: {prizeLine(payload)}</p>
          </>
        ) : (
          <p className="mail-hint">Fix the highlighted fields to see the preview.</p>
        )}
      </div>

      <div className="mail-box">
        <strong>How prizes are actually paid</strong>
        <p className="mail-hint">A prize can be lower than the number you set, or not paid at all:</p>
        <ul className="te-notes">
          <li>Only players who saved at least one answer and got at least one right can win a prize.</li>
          <li>1st prize is paid only when at least two players saved an answer.</li>
          <li>A position pays only if that many players took part: 2nd needs two players in the room, 3rd needs three.</li>
          <li>A room never pays out more than the coins paid by the players who entered the lobby (the host counts only if they entered too). If the prizes add up to more, the lowest positions are reduced first (3rd, then 2nd, then 1st).</li>
        </ul>
      </div>
    </div>
  );
};

export default TeamExamSettingsPage;
